/**
 * InvoicePdfAuditDrawer - Right-side panel for auditing and batch-enqueueing paid invoice PDFs
 */

import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { FactCheck, PlayArrow } from "@mui/icons-material";
import { logError, logInfo } from "@/shared/utils/logger.js";
import {
  auditPaidInvoicePdfs,
  enqueueMissingPaidInvoicePdfs,
} from "../services/paidPdfAuditService.js";

const DEFAULT_SINCE_DAYS = 180;
const DEFAULT_MAX_SCAN = 5000;
const DEFAULT_LIMIT = 200;

export default function InvoicePdfAuditDrawer({ open, onClose, jobId }) {
  const [sinceDays, setSinceDays] = useState(DEFAULT_SINCE_DAYS);
  const [maxScan, setMaxScan] = useState(DEFAULT_MAX_SCAN);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [checkFirebase, setCheckFirebase] = useState(false);

  const [auditLoading, setAuditLoading] = useState(false);
  const [enqueueLoading, setEnqueueLoading] = useState(false);
  const [error, setError] = useState(null);
  const [auditResult, setAuditResult] = useState(null);
  const [lastEnqueueResult, setLastEnqueueResult] = useState(null);

  const missingInvoiceIds = useMemo(() => {
    const missingMeta = auditResult?.missingMetadata || [];
    const missingFb = auditResult?.missingFirebase || [];
    const ids = [
      ...missingMeta.map((x) => x.invoiceId),
      ...missingFb.map((x) => x.invoiceId),
    ].filter(Boolean);
    return Array.from(new Set(ids));
  }, [auditResult]);

  const handleAudit = async () => {
    try {
      setAuditLoading(true);
      setError(null);
      setLastEnqueueResult(null);

      const res = await auditPaidInvoicePdfs({
        sinceDays,
        maxScan,
        limit,
        checkFirebase,
        jobId: jobId || null,
      });

      setAuditResult(res);
      logInfo("Paid invoice PDF audit completed", {
        scanned: res?.meta?.scanned,
        settledCount: res?.meta?.settledCount,
        missingMetadataCount: res?.missingMetadata?.length,
        missingFirebaseCount: res?.missingFirebase?.length,
      });
    } catch (e) {
      setError(e.message || "Failed to audit invoices");
      logError("Paid invoice PDF audit failed", { error: e.message });
    } finally {
      setAuditLoading(false);
    }
  };

  const handleEnqueueBatch = async () => {
    try {
      setEnqueueLoading(true);
      setError(null);

      if (!missingInvoiceIds.length) {
        setError("No missing PDFs found. Run audit first.");
        return;
      }

      const res = await enqueueMissingPaidInvoicePdfs({
        invoiceIds: missingInvoiceIds,
        reason: "ui-audit-batch",
      });

      setLastEnqueueResult(res);
      logInfo("Enqueued paid invoice PDF batch", {
        enqueued: res?.enqueued,
        skipped: res?.skipped,
      });
    } catch (e) {
      setError(e.message || "Failed to enqueue batch");
      logError("Enqueue paid invoice PDF batch failed", { error: e.message });
    } finally {
      setEnqueueLoading(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: 360, sm: 420 }, p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <FactCheck color="primary" />
          <Typography variant="h6">Invoice PDF Audit</Typography>
        </Box>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Find settled invoices missing a stored paid PDF snapshot, then enqueue
          generation in bulk.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField
            label="Since (days)"
            type="number"
            value={sinceDays}
            onChange={(e) => setSinceDays(Number(e.target.value || 0))}
            inputProps={{ min: 1, max: 3650 }}
            size="small"
          />
          <TextField
            label="Max scan"
            type="number"
            value={maxScan}
            onChange={(e) => setMaxScan(Number(e.target.value || 0))}
            inputProps={{ min: 100, max: 20000 }}
            size="small"
          />
          <TextField
            label="Result limit"
            type="number"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value || 0))}
            inputProps={{ min: 10, max: 2000 }}
            size="small"
          />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={checkFirebase}
                  onChange={(e) => setCheckFirebase(e.target.checked)}
                />
              }
              label="Check Firebase"
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button
            variant="contained"
            startIcon={
              auditLoading ? <CircularProgress size={18} /> : <FactCheck />
            }
            onClick={handleAudit}
            disabled={auditLoading || enqueueLoading}
            sx={{ textTransform: "none", fontWeight: 600 }}
            fullWidth
          >
            Audit
          </Button>
          <Button
            variant="outlined"
            startIcon={
              enqueueLoading ? <CircularProgress size={18} /> : <PlayArrow />
            }
            onClick={handleEnqueueBatch}
            disabled={enqueueLoading || auditLoading || !missingInvoiceIds.length}
            sx={{ textTransform: "none", fontWeight: 600 }}
            fullWidth
          >
            Enqueue Batch
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        {auditResult ? (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Summary
            </Typography>
            <Box sx={{ display: "grid", gap: 0.5, mb: 2 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Scanned: <strong>{auditResult?.meta?.scanned ?? 0}</strong> •
                Settled: <strong>{auditResult?.meta?.settledCount ?? 0}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Missing metadata:{" "}
                <strong>{auditResult?.missingMetadata?.length ?? 0}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Missing Firebase file:{" "}
                <strong>{auditResult?.missingFirebase?.length ?? 0}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Total missing (deduped):{" "}
                <strong>{missingInvoiceIds.length}</strong>
              </Typography>
            </Box>

            {lastEnqueueResult && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Enqueued: <strong>{lastEnqueueResult.enqueued}</strong> • Skipped:{" "}
                <strong>{lastEnqueueResult.skipped}</strong>
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Missing invoices (sample)
            </Typography>
            <List dense sx={{ maxHeight: 320, overflow: "auto", borderRadius: 1 }}>
              {(auditResult?.missingMetadata || [])
                .slice(0, 50)
                .map((row) => (
                  <ListItem key={`meta-${row.invoiceId}`} divider>
                    <ListItemText
                      primary={row.invoiceId}
                      secondary={`${row.customerName || "Unknown"} • Job ${
                        row.jobId || "N/A"
                      }`}
                    />
                  </ListItem>
                ))}
              {(auditResult?.missingFirebase || [])
                .slice(0, 50)
                .map((row) => (
                  <ListItem key={`fb-${row.invoiceId}`} divider>
                    <ListItemText
                      primary={row.invoiceId}
                      secondary={`Missing file • ${row.customerName || "Unknown"}`}
                    />
                  </ListItem>
                ))}
              {!auditResult?.missingMetadata?.length &&
                !auditResult?.missingFirebase?.length && (
                  <ListItem>
                    <ListItemText primary="No missing PDFs found." />
                  </ListItem>
                )}
            </List>
          </Box>
        ) : (
          <Alert severity="info">
            Run an audit to see which settled invoices are missing stored PDFs.
          </Alert>
        )}
      </Box>
    </Drawer>
  );
}

